// src/component/ProfileOrders.jsx
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import { authFetch } from "../utils/authFetch";

/**
 * Список заказов пользователя (личный кабинет)
 * Совместим с форматом:
 *  - status: { id, code, title, ... }   ← новый (правильный)
 *  - status: "delivered" | "new" ...     ← на всякий случай (обратная совместимость)
 */
export default function ProfileOrders({ active }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openedId, setOpenedId] = useState(null);

  useEffect(() => {
    if (!active) return;
    setLoading(true);
    authFetch("/api/orders/my")
      .then(({ data }) => setOrders(Array.isArray(data) ? data : []))
      .finally(() => setLoading(false));
  }, [active]);

  if (!active) return null;

  // ---------- helpers ----------
  const statusCode = (order) =>
    (order?.status && typeof order.status === "object"
      ? order.status.code
      : order?.status) || ""; // например "delivered"

  const statusTitle = (order) =>
    (order?.status && typeof order.status === "object"
      ? order.status.title
      : order?.status) || "—";

  const statusBadgeStyle = (code) => {
    // Цветовая индикация по коду
    if (code === "delivered") {
      return { background: "#e7f7df", color: "#2f7a13" }; // зеленый
    }
    if (code === "canceled") {
      return { background: "#ffeaea", color: "#a12222" }; // красный
    }
    // new / assembling / processing / delivery / completed ...
    return { background: "#fff4e0", color: "#b45309" }; // желтый/оранжевый
  };

  const fmtDate = (iso) =>
    iso ? dayjs(iso).locale("ru").format("DD MMMM YYYY, HH:mm") : "—";

  // ---------- UI ----------
  return (
    <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-lg p-6">
      <h1 className="text-2xl font-bold text-orange-600 mb-4">Мои заказы</h1>

      {loading ? (
        <p className="text-gray-500 text-center">Загрузка…</p>
      ) : orders.length === 0 ? (
        <p className="text-gray-500 text-center">У вас пока нет заказов.</p>
      ) : (
        <ul className="space-y-4">
          {orders.map((order) => {
            const opened = openedId === order.id;
            const code = statusCode(order);
            const title = statusTitle(order);
            const badgeStyle = statusBadgeStyle(code);

            return (
              <li
                key={order.id}
                className={`transition cursor-pointer border-2 ${
                  opened
                    ? "border-orange-500 bg-orange-50"
                    : "border-gray-100 bg-white"
                } rounded-xl shadow-sm hover:shadow-lg hover:border-orange-400`}
                onClick={() => setOpenedId(opened ? null : order.id)}
              >
                {/* Шапка заказа */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between p-4">
                  <div>
                    <span className="text-sm text-gray-400">Заказ</span>
                    <div className="font-bold text-lg text-gray-800">
                      №{order.order_number || order.id}
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row md:gap-8 mt-2 md:mt-0">
                    <div>
                      <span className="text-sm text-gray-400">Сумма</span>
                      <div className="text-orange-600 font-bold text-lg">
                        {Number(order.total_price || 0).toLocaleString("ru-RU")} ₽
                      </div>
                    </div>

                    <div>
                      <span className="text-sm text-gray-400">Статус</span>
                      <div>
                        <span
                          className="text-xs font-semibold rounded px-2 py-1 mt-1 inline-block"
                          style={{ ...badgeStyle, minWidth: 80, textAlign: "center" }}
                          title={code}
                        >
                          {title}
                        </span>
                      </div>
                    </div>

                    <div>
                      <span className="text-sm text-gray-400">Дата</span>
                      <div className="text-gray-700 font-semibold">
                        {fmtDate(order.created_at)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Детали заказа */}
                {opened && (
                  <div className="bg-white border-t mt-2 px-6 pb-6 pt-2 text-sm text-gray-700 animate-fade-in">
                    <div className="flex flex-col gap-1 mb-4">
                      <div>
                        <span className="text-gray-400">Способ получения: </span>
                        <span className="font-semibold">
                          {order.delivery_method === "delivery"
                            ? "Доставка"
                            : "Самовывоз"}
                        </span>
                      </div>

                      {order.delivery_method === "delivery" && (
                        <div>
                          <span className="text-gray-400">Адрес доставки: </span>
                          <span>{order.address || "—"}</span>
                        </div>
                      )}

                      {order.delivery_method === "pickup" && (
                        <div>
                          <span className="text-gray-400">Пункт самовывоза: </span>
                          <span>{order.store_location || "—"}</span>
                        </div>
                      )}

                      <div>
                        <span className="text-gray-400">ФИО: </span>
                        <span>
                          {(order.last_name || "") +
                            " " +
                            (order.first_name || "") +
                            " " +
                            (order.patronymic || "")}
                        </span>
                      </div>

                      <div>
                        <span className="text-gray-400">Телефон: </span>
                        <span>{order.phone || "—"}</span>
                      </div>

                      <div>
                        <span className="text-gray-400">E-mail: </span>
                        <span>{order.email || "—"}</span>
                      </div>

                      {order.comment ? (
                        <div>
                          <span className="text-gray-400">Комментарий: </span>
                          <span>{order.comment}</span>
                        </div>
                      ) : null}
                    </div>

                    {/* Состав заказа */}
                    <div className="pt-2 mt-2 border-t-2 border-orange-100">
                      <div className="font-bold mb-2 text-gray-500 text-base">
                        Состав заказа:
                      </div>
                      <ul className="space-y-1 list-disc list-inside">
                        {(order.items || []).map((item) => (
                          <li key={item.id}>
                            {item.product_name || "Товар"} — {item.quantity} шт.
                            {item.price ? ` (${Number(item.price).toLocaleString("ru-RU")} ₽)` : ""}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
